export const DEFAULT_LOCALE = "en";
export const PSEUDO_LOCALE = "x-pseudo";
// Terminology-only variant: the server wraps just the entity.* keys, so QA can
// see which text Site Terminology overrides drive without the noise of every
// keyed string. Caption-level transforms treat both variants the same (the
// me.customCaptions blob is terminology by definition).
export const PSEUDO_TERMS_LOCALE = "x-pseudo-terms";

export const isPseudoTag = (tag) =>
	tag === PSEUDO_LOCALE || tag === PSEUDO_TERMS_LOCALE;
export const I18N_NAMESPACE = "translation";
export const LOCALE_STORAGE_KEY = "i18nLocale";
// The user/site-chosen UI locale, persisted in localStorage across all
// environments (distinct from the dev-only pseudo key above).
export const APP_LOCALE_KEY = "appLocale";

// The pseudo-locale toggle is QA tooling. It is available on a local dev
// server (NODE_ENV === "development" — no env var needed) and on the deployed
// dev environment (REACT_APP_ENVIRONMENT === "dev", set only in .env.dev).
// A production/staging build has neither signal, so it can never open.
export const isPseudoLocaleEnvironment = (
	nodeEnv = process.env.NODE_ENV,
	appEnvironment = process.env.REACT_APP_ENVIRONMENT
) =>
	nodeEnv === "development" ||
	(appEnvironment ?? "").trim().toLowerCase() === "dev";

// Mirrors the server's locale-tag guard so a bad ?locale= query value falls
// back to English instead of producing a 400 from the bundle endpoint.
const LOCALE_TAG_PATTERN = /^[A-Za-z0-9-]{1,20}$/;

// Client-side twin of the server's PseudoLocale.Wrap (PseudoLocale.cs) —
// same accent tables, same [!! !!] markers — used to pseudo-ize values that
// bypass the bundle (the legacy me.customCaptions blob) so terminology-driven
// text reads as "will translate" during pseudo QA, while genuinely hardcoded
// copy stays plain.
const ACCENTED_LOWER = "áƀçđéƒǧĥíĵǩłɱñóƥɋřšťúṽẘẋýž";
const ACCENTED_UPPER = "ÁƁÇĐÉƑǦĤÍĴǨŁϺÑÓƤɊŘŠŤÚṼẄẊÝŽ";

export const PSEUDO_PREFIX = "[!!";
export const PSEUDO_SUFFIX = "!!]";

// Wraps a string in the pseudo markers with accented letters. Idempotent: an
// already-wrapped string is returned unchanged, so the same value can pass
// through the transform more than once (e.g. a reducer re-applying it to state
// it already transformed) without nesting "[!![!!X!!]!!]". This transform is
// never reversed — genuine captions may already contain accented characters,
// so reversing would be lossy; callers that need the original keep the
// untransformed value instead (see pseudoStorageShim).
export const pseudoizeText = (value) => {
	if (value.startsWith(PSEUDO_PREFIX) && value.endsWith(PSEUDO_SUFFIX)) {
		return value;
	}

	let wrapped = "";

	for (const c of value) {
		if (c >= "a" && c <= "z") {
			wrapped += ACCENTED_LOWER[c.charCodeAt(0) - 97];
		} else if (c >= "A" && c <= "Z") {
			wrapped += ACCENTED_UPPER[c.charCodeAt(0) - 65];
		} else {
			wrapped += c;
		}
	}

	return `${PSEUDO_PREFIX}${wrapped}${PSEUDO_SUFFIX}`;
};

// Exact inverse of pseudoizeText, applied ONLY to marker-wrapped strings. The
// accent tables are bijective and the [!! !!] markers are unambiguous, so a
// wrapped value converts back to its original losslessly; anything unmarked is
// returned untouched (this is where reversal WOULD be lossy - a genuine caption
// may legitimately contain accented characters). Needed because a wrapped value
// can leak into a me write that the shim cannot reconcile (e.g. copying the blob
// into a store that was empty) - without an inverse it would be baked in as the
// "real" caption and survive turning pseudo off.
const REVERSE_ACCENTS = (() => {
	const map = {};
	for (let i = 0; i < 26; i++) {
		map[ACCENTED_LOWER[i]] = String.fromCharCode(97 + i);
		map[ACCENTED_UPPER[i]] = String.fromCharCode(65 + i);
	}
	return map;
})();

export const depseudoizeText = (value) => {
	if (
		typeof value !== "string" ||
		!value.startsWith(PSEUDO_PREFIX) ||
		!value.endsWith(PSEUDO_SUFFIX)
	) {
		return value;
	}

	const inner = value.slice(PSEUDO_PREFIX.length, -PSEUDO_SUFFIX.length);
	let restored = "";

	for (const c of inner) {
		restored += REVERSE_ACCENTS[c] ?? c;
	}

	return restored;
};

export const depseudoizeCustomCaptions = (captions) =>
	Object.fromEntries(
		Object.entries(captions).map(([key, value]) => [
			key,
			depseudoizeText(value),
		])
	);

export const pseudoizeCustomCaptions = (captions) =>
	Object.fromEntries(
		Object.entries(captions).map(([key, value]) => [
			key,
			typeof value === "string" && value !== ""
				? pseudoizeText(value)
				: value,
		])
	);

// Recursively walks an API payload and applies mapFn(fieldName, value) to every
// *CC caption field (site-app details carry caption columns at the top level and
// nested under .application). Only keys ending in "CC" are touched, so user
// content like application.name stays readable.
export const mapCaptionCCFields = (value, mapFn) => {
	if (Array.isArray(value)) {
		return value.map((v) => mapCaptionCCFields(v, mapFn));
	}

	if (value === null || typeof value !== "object") {
		return value;
	}

	return Object.fromEntries(
		Object.entries(value).map(([key, entry]) => {
			if (typeof entry === "string" && entry !== "" && key.endsWith("CC")) {
				return [key, mapFn(key, entry)];
			}

			if (entry && typeof entry === "object") {
				return [key, mapCaptionCCFields(entry, mapFn)];
			}

			return [key, entry];
		})
	);
};

export const pseudoizeCaptionCCFields = (value) =>
	mapCaptionCCFields(value, (_key, str) => pseudoizeText(str));

// The pseudo variant currently switched on for this session (via the dev
// toggle's persisted choice or an explicit ?locale=), or null. Always null
// outside pseudo-capable environments.
export const getActivePseudoTag = () => {
	if (!isPseudoLocaleEnvironment()) return null;

	try {
		// Same precedence as resolveInitialLocale: an explicit ?locale= always
		// decides; the toggle's persisted choice applies otherwise.
		const requested = new URLSearchParams(window.location.search).get(
			"locale"
		);
		if (requested) {
			return isPseudoTag(requested) ? requested : null;
		}

		const stored = window.sessionStorage.getItem(LOCALE_STORAGE_KEY);
		return isPseudoTag(stored) ? stored : null;
	} catch {
		return null;
	}
};

export const isPseudoLocaleActive = () => getActivePseudoTag() !== null;

// The bundle endpoint encodes plural terms as objects keyed by CLDR plural
// category ({ one: "Asset", other: "Assets" }) rather than ICU strings.
// i18next wants one resource per category via key suffixes
// (entity.asset_one / entity.asset_other) and picks the category itself
// through Intl.PluralRules. The bare key is also written (singular form
// preferred) so t(key) without a count still resolves.
export const bundleMessagesToResources = (messages = {}) => {
	const resources = {};

	Object.entries(messages).forEach(([key, value]) => {
		if (value !== null && typeof value === "object") {
			Object.entries(value).forEach(([category, form]) => {
				resources[`${key}_${category}`] = form;
			});

			resources[key] =
				value.one ?? value.other ?? Object.values(value)[0] ?? "";
		} else {
			resources[key] = value;
		}
	});

	return resources;
};

// Locale resolution for app start: an explicit ?locale= query param wins
// (this is also how the server-generated pseudo-locale is activated, via
// ?locale=x-pseudo), then a locale persisted by the pseudo toggle; anything
// absent or malformed resolves to English.
export const resolveInitialLocale = (search, storedLocale = null) => {
	const requested = new URLSearchParams(search).get("locale");

	if (requested && LOCALE_TAG_PATTERN.test(requested)) {
		return requested;
	}

	if (storedLocale && LOCALE_TAG_PATTERN.test(storedLocale)) {
		return storedLocale;
	}

	return DEFAULT_LOCALE;
};
